import React, { useState, useEffect } from 'react';
import { useOrganization } from '@clerk/clerk-react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import { Mail, RefreshCw, CheckCircle, Server } from 'lucide-react';

// Configuración de email GLOBAL de Sera (chat + mensajes de pedido / tickets).
// Guarda en org_email_configs; el servicio compartido emails.send_email respeta
// el SMTP propio del tenant si está activo, o usa Resend (dominio Sera) por defecto.
export const EmailConfigSection = () => {
  const { organization } = useOrganization();
  const [config, setConfig] = useState({
    use_custom_smtp: false,
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    from_name: '',
    from_email: '',
    inactivity_minutes: 2,
    visitor_notifications_enabled: true,
    smtp_password_set: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    if (!organization?.id) return;
    api.get('/api/chat/email-config')
      .then(r => setConfig(prev => ({ ...prev, ...r.data.config })))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [organization?.id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/chat/email-config', config);
      toast.success('Configuración de email guardada');
    } catch {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail.trim() || !testEmail.includes('@')) {
      toast.error('Introduce un email válido');
      return;
    }
    setTesting(true);
    try {
      await api.post('/api/chat/email-config/test', { email: testEmail });
      toast.success(`Email de prueba enviado a ${testEmail}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Error al enviar. Revisa la configuración SMTP.';
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const upd = (key, val) => setConfig(prev => ({ ...prev, [key]: val }));

  if (loading) {
    return (
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 flex items-center justify-center">
        <RefreshCw size={18} className="animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 space-y-6" data-testid="email-config-section">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
          <Mail size={18} className="text-indigo-400" />
        </div>
        <div>
          <h3 className="text-white font-semibold">Notificaciones por email</h3>
          <p className="text-gray-400 text-sm mt-0.5">
            Configuración global de envío de emails de Sera: chat y mensajes de pedido (tickets).
            Por defecto se envían desde el dominio de Sera (Resend); activa tu SMTP para enviar desde tu dominio.
          </p>
        </div>
      </div>

      {/* Provider section */}
      <div className="bg-gray-950/50 border border-gray-800 rounded-xl p-5">
        <h4 className="text-white font-medium text-sm mb-1 flex items-center gap-2">
          <Server size={14} className="text-indigo-400" /> Proveedor de email
        </h4>
        <p className="text-gray-500 text-xs mb-4">
          Activa SMTP propio para que TODOS los emails de Sera salgan desde tu servidor y dominio.
        </p>

        {/* Nombre del remitente — siempre visible, aplica a Resend y SMTP */}
        <div className="mb-4">
          <label className="text-gray-400 text-xs block mb-1">Nombre del remitente</label>
          <input
            value={config.from_name}
            onChange={e => upd('from_name', e.target.value)}
            placeholder="Ej: Soporte Unbraked"
            data-testid="from-name-input"
            className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
          />
          <p className="text-gray-600 text-xs mt-1">
            Nombre que verá el cliente en el campo "De:" del email. Si está vacío, se usa el nombre de tu organización.
          </p>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <Switch
            data-testid="use-custom-smtp-toggle"
            checked={config.use_custom_smtp}
            onCheckedChange={v => upd('use_custom_smtp', v)}
          />
          <span className="text-sm text-white">Usar mi servidor SMTP</span>
          {!config.use_custom_smtp && (
            <span className="text-indigo-400 text-xs ml-1">(actualmente: Resend · dominio Sera)</span>
          )}
        </div>

        {config.use_custom_smtp && (
          <div className="space-y-3 pt-3 border-t border-gray-800">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-gray-400 text-xs block mb-1">Servidor SMTP</label>
                <input
                  value={config.smtp_host}
                  onChange={e => upd('smtp_host', e.target.value)}
                  placeholder="smtp.tudominio.com"
                  data-testid="smtp-host-input"
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="text-gray-400 text-xs block mb-1">Puerto</label>
                <input
                  type="number"
                  value={config.smtp_port}
                  onChange={e => upd('smtp_port', parseInt(e.target.value) || 587)}
                  data-testid="smtp-port-input"
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-indigo-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-gray-400 text-xs block mb-1">Usuario SMTP</label>
                <input
                  value={config.smtp_user}
                  onChange={e => upd('smtp_user', e.target.value)}
                  placeholder="notificaciones@tudominio.com"
                  data-testid="smtp-user-input"
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="text-gray-400 text-xs block mb-1">
                  Contraseña SMTP {config.smtp_password_set && <span className="text-green-400">(configurada)</span>}
                </label>
                <input
                  type="password"
                  value={config.smtp_password}
                  onChange={e => upd('smtp_password', e.target.value)}
                  placeholder={config.smtp_password_set ? '••••••••' : 'Contraseña'}
                  data-testid="smtp-password-input"
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
                />
              </div>
            </div>
            <div>
              <label className="text-gray-400 text-xs block mb-1">Email del remitente</label>
              <input
                value={config.from_email}
                onChange={e => upd('from_email', e.target.value)}
                placeholder="noreply@tudominio.com"
                data-testid="from-email-input"
                className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
              />
            </div>
          </div>
        )}
      </div>

      {/* Chat visitor notifications */}
      <div className="bg-gray-950/50 border border-gray-800 rounded-xl p-5">
        <div className="flex items-center justify-between mb-1">
          <div>
            <h4 className="text-white font-medium text-sm">Notificaciones de chat al visitante</h4>
            <p className="text-gray-500 text-xs mt-1">
              Cuando el agente responde en el chat y el visitante está offline, se le envía un email automático.
            </p>
          </div>
          <Switch
            data-testid="visitor-notif-toggle"
            checked={config.visitor_notifications_enabled}
            onCheckedChange={v => upd('visitor_notifications_enabled', v)}
          />
        </div>

        {config.visitor_notifications_enabled && (
          <div className="mt-4 pt-4 border-t border-gray-800">
            <label className="text-gray-400 text-xs block mb-1">
              Minutos de inactividad antes de enviar email
            </label>
            <div className="flex items-center gap-3">
              <input
                type="number" min="1" max="60"
                value={config.inactivity_minutes}
                onChange={e => upd('inactivity_minutes', parseInt(e.target.value) || 2)}
                data-testid="inactivity-minutes-input"
                className="w-20 bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-indigo-500"
              />
              <span className="text-gray-500 text-xs">minutos (por defecto: 2)</span>
            </div>
          </div>
        )}
      </div>

      {/* Test email */}
      <div className="bg-gray-950/50 border border-gray-800 rounded-xl p-5">
        <h4 className="text-white font-medium text-sm mb-1">Enviar email de prueba</h4>
        <p className="text-gray-500 text-xs mb-3">Verifica que la configuración funciona correctamente.</p>
        <div className="flex gap-2">
          <input
            type="email"
            value={testEmail}
            onChange={e => setTestEmail(e.target.value)}
            placeholder="tu@email.com"
            data-testid="test-email-input"
            className="flex-1 bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
          />
          <Button size="sm" onClick={sendTest} disabled={testing}
            data-testid="send-test-email-btn"
            className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs gap-1">
            {testing ? <RefreshCw size={11} className="animate-spin" /> : <Mail size={11} />}
            {testing ? 'Enviando...' : 'Enviar prueba'}
          </Button>
        </div>
      </div>

      <Button onClick={save} disabled={saving}
        data-testid="save-email-config-btn"
        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white gap-2">
        {saving ? <RefreshCw size={14} className="animate-spin" /> : <CheckCircle size={14} />}
        {saving ? 'Guardando...' : 'Guardar configuración'}
      </Button>
    </div>
  );
};
